import { ExpensesBreakdown } from 'types';
import { moneyFormatter, getPercentageFromTotal } from 'utils';
import { BarItem } from './BarItem';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Edit } from '../../assets/icons';

export const BreakdownChart = ({
  employerPays,
  employeeGets,
  onGrossSalaryChange,
}: {
  employerPays: ExpensesBreakdown;
  employeeGets: ExpensesBreakdown;
  onGrossSalaryChange: (updatedVal: number | string) => unknown;
}) => {
  const [gross, setGross] = useState(String(employerPays.gross.toFixed(0)));

  const [isGrossInput, setGrossIsInput] = useState(false);
  const grossRef = useRef<null | HTMLInputElement>(null);

  useEffect(() => {
    setGross(String(employerPays.gross.toFixed(0)));
  }, [employerPays.gross]);

  return (
    <div className="mb-6">
      <div className="text-xl font-medium mt-4 mb-6 text-center">
        Total employment cost breakdown
      </div>
      <div className="w-full flex flex-col gap-[6px]">
        {/* total block */}
        <div className="w-full">
          <BarItem width="100%" background="#F5F5EB">
            Total Cost of Employment:
            <div className="font-medium text-center">
              {moneyFormatter(employerPays.total, 'USD', 0)}
            </div>
          </BarItem>
        </div>

        <div className="w-full flex gap-[6px]">
          {/* left breakdown */}
          <div
            style={{
              width: getPercentageFromTotal(
                employerPays.total,
                employerPays.gross
              ),
              flex: 1,
            }}
            className="w-full flex flex-col gap-[6px] ease-in-out duration-300"
          >
            <BarItem width="100%" background="#fac8e1">
              Gross Salary:
              <div
                title="click to edit"
                className="cursor-pointer flex gap-[6px] items-center"
                onClick={() => {
                  setGrossIsInput(true);
                  grossRef.current?.focus();
                }}
              >
                <Edit width={14} height={14} />
                <input
                  ref={grossRef}
                  className={`text-center font-medium transition w-full border border-e3 focus:outline-none focus:border-primary rounded-md min-h-[24px] max-h-[24px] max-w-[90px] ${
                    isGrossInput ? 'static' : 'absolute -z-[1]'
                  }`}
                  type="text"
                  onChange={({
                    target: { value },
                  }: ChangeEvent<HTMLInputElement>) => {
                    const castedToNumber = Number(value);

                    if (value === '') return setGross('');
                    if (isNaN(castedToNumber)) return;

                    setGross(value);
                  }}
                  onKeyDown={({ key }) => {
                    if (key === 'Enter' || key === 'Escape') {
                      grossRef.current?.blur();
                    }
                  }}
                  onBlur={() => {
                    setGrossIsInput(false);

                    if (gross !== employerPays.gross.toFixed(0)) {
                      onGrossSalaryChange(gross);
                    }
                  }}
                  value={gross}
                />
                <div
                  className={`font-medium text-center ${
                    isGrossInput ? 'absolute -z-[1]' : 'static'
                  }`}
                >
                  {moneyFormatter(gross, 'USD', 0)}
                </div>
              </div>
            </BarItem>

            <div className="w-full flex gap-[6px]">
              <BarItem
                width={getPercentageFromTotal(
                  employerPays.gross,
                  employeeGets.total
                )}
                classNames="bg-green"
              >
                Net Salary:
                <div className="font-medium">
                  {moneyFormatter(employeeGets.total, 'USD', 0)}
                </div>
              </BarItem>

              <BarItem
                width={getPercentageFromTotal(
                  employerPays.gross,
                  employeeGets.taxes
                )}
                classNames="bg-lightGreen"
              >
                Taxes paid by employee:
                <div className="font-medium">
                  {moneyFormatter(employeeGets.taxes, 'USD', 0)}
                </div>
              </BarItem>
            </div>
          </div>

          {/* right breakdown */}
          <div
            style={{
              width: getPercentageFromTotal(
                employerPays.total,
                employerPays.taxes
              ),
              // minWidth: 'min-content',
              maxWidth: 'max-content',
              flex: 1,
            }}
            className="w-full flex flex-col gap-[6px] ease-in-out duration-300"
          >
            <BarItem width="100%" background="#fde9f3">
              Taxes paid by employer:
              <div className="font-medium">
                {moneyFormatter(employerPays.taxes, 'USD', 0)}
              </div>
            </BarItem>
          </div>
        </div>
      </div>
    </div>
  );
};
